import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * PowerHuds "online player camera" sender page.
 *
 * Deliberately UNLISTED: the route (/camera/:token) is registered outside the
 * Header/Footer layout in App.tsx (same pattern as /clips/overlay) and linked
 * from nowhere — players only ever get here via the tokenized URL that the
 * broadcast operator copies out of PowerHuds. Normal site visitors never see
 * it.
 *
 * Transport: native WebSocket to the PowerLigaStats camera relay
 * (/ws/camera/player). The room token is sent as the FIRST message — never in
 * the URL of the WS handshake, which the backend request-logs. The relay only
 * shuttles opaque {event,args} envelopes between this page and the operator's
 * PowerHuds instance; the WebRTC video itself flows peer-to-peer.
 *
 * WebRTC logic is a port of PowerHuds' LAN sender page
 * (PowerHuds/src/electron/api/v2/cameras/senderPage.ts): one non-trickle
 * RTCPeerConnection per HUD client, answer-timeout re-offer with capped
 * backoff, live device switch via replaceTrack.
 */

const ACCENT = "#f15c25";
const MAX_RETRIES = 8;
const ANSWER_TIMEOUT_MS = 8000;
/** Direct-only. Relay servers are added to this ONLY after a direct attempt
 *  has failed — a LAN event therefore never requests (or costs) a relay. */
const DIRECT_ICE: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

/** wss://api.powerstats.dk/ws/camera/player — derived from the API base with
 *  the legacy /PowerStats path prefix stripped (the WS endpoints live at the
 *  server root, the prefix is a Cloudflare-edge reconciliation for old REST
 *  paths only). */
function playerWsUrl(): string {
  const apiBase = (process.env.REACT_APP_API_URL || "http://localhost:5270")
    .replace(/\/PowerStats\/?$/i, "")
    .replace(/\/$/, "");
  return apiBase.replace(/^http/i, "ws") + "/ws/camera/player";
}

interface RosterEntry {
  steamid: string;
  label: string;
}

/**
 * Fixed-delay video buffer.
 *
 * Online matches are observed over GOTV, so gameplay reaches viewers
 * ~tv_delay seconds late while a webcam is instant — the player reacts
 * before the audience sees why. We hold the video here, on the sender's
 * machine: MediaRecorder encodes the live camera, MediaSource plays it back
 * N seconds behind, and captureStream() re-exposes that delayed playback as
 * the track we transmit. Buffering *encoded* video costs ~1.5 Mbit/s (90 s ≈
 * 17 MB) instead of the gigabytes raw frames would need, and because it
 * happens pre-transmission every HUD gets delayed video with no HUD support.
 */
interface DelayBuffer {
  stream: MediaStream;
  started: boolean;
  destroy: () => void;
}

function pickDelayMime(): string | null {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported(m) &&
      typeof MediaSource !== "undefined" &&
      MediaSource.isTypeSupported(m)
    ) {
      return m;
    }
  }
  return null;
}

function createDelayBuffer(
  source: MediaStream,
  seconds: number,
  onFail: (message: string) => void,
  onStarted: () => void,
): DelayBuffer | null {
  const mime = pickDelayMime();
  if (!mime) {
    onFail("This browser can't buffer video for delay.");
    return null;
  }

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  // Kept in the DOM but invisible — a detached/display:none element can have
  // its rendering optimized away, which starves captureStream().
  video.style.cssText =
    "position:fixed;width:2px;height:2px;opacity:0;pointer-events:none;left:0;bottom:0;";
  document.body.appendChild(video);

  const mediaSource = new MediaSource();
  video.src = URL.createObjectURL(mediaSource);

  const timers: number[] = [];
  let recorder: MediaRecorder | null = null;
  let sourceBuffer: SourceBuffer | null = null;
  let destroyed = false;
  const queue: ArrayBuffer[] = [];

  const buffer: DelayBuffer = {
    stream: null as unknown as MediaStream,
    started: false,
    destroy: () => {
      destroyed = true;
      timers.forEach((t) => {
        window.clearInterval(t);
        window.clearTimeout(t);
      });
      try {
        if (recorder && recorder.state !== "inactive") recorder.stop();
      } catch {
        /* already stopped */
      }
      try {
        video.pause();
      } catch {
        /* ignore */
      }
      video.remove();
    },
  };

  // Everything below is EVENT-driven, with an interval only as a backstop:
  // the player is in CS2 fullscreen, so this page is a hidden tab and Chrome
  // throttles its timers (to ~1/s, and 1/min after a few minutes).
  // `updateend` fires off the recorder's data path and `timeupdate` off the
  // media pipeline, and neither is throttled that way.
  const maybeStart = () => {
    if (destroyed || buffer.started) return;
    const b = video.buffered;
    if (b.length === 0) return;
    if (b.end(b.length - 1) - b.start(0) < seconds) return;
    buffer.started = true;
    try {
      video.currentTime = b.start(0);
    } catch {
      /* ignore */
    }
    video.play().catch(() => undefined);
    onStarted();
  };

  const holdDelay = () => {
    if (destroyed || !buffer.started || !sourceBuffer) return;
    const b = video.buffered;
    if (b.length === 0) return;
    const target = b.end(b.length - 1) - seconds;
    const drift = video.currentTime - target;
    if (Math.abs(drift) > 3) {
      try {
        video.currentTime = Math.max(b.start(0), target);
      } catch {
        /* ignore */
      }
      video.playbackRate = 1;
    } else if (drift < -0.4) {
      video.playbackRate = 1.03;
    } else if (drift > 0.4) {
      video.playbackRate = 0.97;
    } else {
      video.playbackRate = 1;
    }
    if (!sourceBuffer.updating && video.currentTime - b.start(0) > 5) {
      try {
        sourceBuffer.remove(b.start(0), video.currentTime - 2);
      } catch {
        /* ignore */
      }
    }
  };

  video.addEventListener("timeupdate", holdDelay);

  const pump = () => {
    if (destroyed || !sourceBuffer || sourceBuffer.updating) return;
    const next = queue.shift();
    if (next) {
      try {
        sourceBuffer.appendBuffer(next);
      } catch {
        /* buffer full or closed */
      }
    }
    maybeStart();
  };

  mediaSource.addEventListener("sourceopen", () => {
    if (destroyed) return;
    try {
      sourceBuffer = mediaSource.addSourceBuffer(mime);
      // 'sequence' makes MediaRecorder's chunk timestamps contiguous —
      // without it the first append can land at a wild offset.
      sourceBuffer.mode = "sequence";
    } catch (e) {
      buffer.destroy();
      onFail("Could not start the delay buffer: " + (e as Error).message);
      return;
    }
    sourceBuffer.addEventListener("updateend", pump);

    recorder = new MediaRecorder(source, {
      mimeType: mime,
      videoBitsPerSecond: 1_500_000,
    });
    recorder.ondataavailable = async (e) => {
      if (destroyed || !e.data || e.data.size === 0) return;
      queue.push(await e.data.arrayBuffer());
      pump();
    };
    recorder.start(250); // small slices keep the buffer smooth

    // Backstop only — the event handlers above do the real work.
    timers.push(
      window.setInterval(() => {
        maybeStart();
        holdDelay();
      }, 1000),
    );

    // Watchdog: a delayed feed that never plays is worse than a live one.
    const watchdog = window.setTimeout(
      () => {
        if (destroyed) return;
        if (!buffer.started || video.currentTime === 0) {
          onFail("Delay buffer didn't start — sending live instead.");
        }
      },
      (seconds + 12) * 1000,
    );
    timers.push(watchdog);
  });

  const captured = (
    video as HTMLVideoElement & { captureStream?: () => MediaStream }
  ).captureStream?.();
  if (!captured) {
    destroyed = true;
    video.remove();
    onFail("This browser can't capture the delayed video.");
    return null;
  }
  buffer.stream = captured;
  return buffer;
}

interface PeerEntry {
  pc: RTCPeerConnection;
  state: string;
  retries: number;
  answerTimer: number | null;
  /** One retry in flight at a time. `connectionstatechange` fires
   *  "disconnected" AND "failed" for the same death, and the answer timeout
   *  can land on top — without this each failure spawned several retries,
   *  each spawning more, until the connection was being torn down and
   *  rebuilt dozens of times a second and ICE never had the ~2s it needs to
   *  complete. */
  retryScheduled: boolean;
}

type Phase =
  | "connecting" // WS dialing / joining
  | "offline" // joined, but the broadcast PC isn't connected
  | "ready" // operator online — can register/stream
  | "kicked" // another device took over
  | "invalid" // bad/expired link
  | "unavailable"; // relay disabled or unreachable

const CameraSenderPage = () => {
  const { token } = useParams<{ token: string }>();

  const [phase, setPhase] = useState<Phase>("connecting");
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [steamid, setSteamid] = useState("");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [peerStates, setPeerStates] = useState<Record<string, string>>({});
  const [delaySeconds, setDelaySeconds] = useState(0);
  const [delayReady, setDelayReady] = useState(false);
  /** Explains a relay fallback (or why one wasn't available). */
  const [relayNote, setRelayNote] = useState("");

  // Mutable connection state lives in refs — socket callbacks must always see
  // current values without re-subscribing.
  const wsRef = useRef<WebSocket | null>(null);
  const peersRef = useRef<Map<string, PeerEntry>>(new Map());
  const streamRef = useRef<MediaStream | null>(null);
  // What peers actually receive: the live camera, or its delayed copy.
  const outgoingRef = useRef<MediaStream | null>(null);
  const delayBufferRef = useRef<DelayBuffer | null>(null);
  const delayRef = useRef(0);
  /** Relay servers, once the operator's PowerHuds has granted them. */
  const turnRef = useRef<RTCIceServer[] | null>(null);
  /** Set after a direct attempt fails, so the next offer asks for a relay. */
  const wantTurnRef = useRef(false);
  /** Resolves the in-flight relay-credential request. */
  const turnResolverRef = useRef<
    ((servers: RTCIceServer[] | null, reason: string | null) => void) | null
  >(null);
  const steamidRef = useRef("");
  const registeredRef = useRef(false);
  const kickedRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const previewRef = useRef<HTMLVideoElement | null>(null);

  const sendEvent = useCallback((event: string, ...args: unknown[]) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ t: "msg", event, args }));
    }
  }, []);

  const renderPeerStates = useCallback(() => {
    const next: Record<string, string> = {};
    peersRef.current.forEach((p, hudId) => {
      next[hudId] = p.state;
    });
    setPeerStates(next);
  }, []);

  const destroyPeer = useCallback(
    (hudId: string) => {
      const p = peersRef.current.get(hudId);
      if (!p) return;
      if (p.pc.connectionState === "connected") {
        // This feed is going off air — don't leave the roster showing green.
        sendEvent("powerhuds:camera_status", { streaming: false, relayed: false });
      }
      if (p.answerTimer) window.clearTimeout(p.answerTimer);
      try {
        p.pc.close();
      } catch {
        /* already closed */
      }
      peersRef.current.delete(hudId);
      renderPeerStates();
    },
    [renderPeerStates, sendEvent],
  );

  const destroyAllPeers = useCallback(() => {
    Array.from(peersRef.current.keys()).forEach(destroyPeer);
  }, [destroyPeer]);

  /**
   * A stream captured from the delay pipeline starts EMPTY — the video
   * element only exposes a track once playback actually begins, which is one
   * full delay-length after buffering starts. Wait for it rather than handing
   * undefined to addTrack/replaceTrack.
   */
  const waitForVideoTrack = useCallback(
    (mediaStream: MediaStream | null, timeoutMs: number): Promise<MediaStreamTrack | null> =>
      new Promise((resolve) => {
        if (!mediaStream) {
          resolve(null);
          return;
        }
        const existing = mediaStream.getVideoTracks()[0];
        if (existing) {
          resolve(existing);
          return;
        }
        const check = () => {
          const track = mediaStream.getVideoTracks()[0];
          if (track) {
            cleanup();
            resolve(track);
          }
        };
        const cleanup = () => {
          window.clearTimeout(timer);
          window.clearInterval(poll);
          mediaStream.removeEventListener("addtrack", check);
        };
        // 'addtrack' is the real signal; the poll is a hidden-tab backstop.
        mediaStream.addEventListener("addtrack", check);
        const poll = window.setInterval(check, 250);
        const timer = window.setTimeout(() => {
          cleanup();
          resolve(null);
        }, timeoutMs);
      }),
    [],
  );

  /** Swap the outgoing track into every live peer — no renegotiation. */
  const swapOutgoingTrack = useCallback(async () => {
    const current = outgoingRef.current;
    const track = await waitForVideoTrack(current, (delayRef.current + 20) * 1000);
    // A newer swap superseded us while we waited.
    if (!track || outgoingRef.current !== current) return;
    peersRef.current.forEach((p) => {
      const sender = p.pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender) sender.replaceTrack(track).catch(() => undefined);
    });
  }, [waitForVideoTrack]);

  /** Build the stream peers should receive for the current delay setting. */
  const buildOutgoing = useCallback(() => {
    delayBufferRef.current?.destroy();
    delayBufferRef.current = null;
    setDelayReady(false);
    const live = streamRef.current;
    if (!live) {
      outgoingRef.current = null;
      return;
    }
    if (delayRef.current <= 0) {
      outgoingRef.current = live;
      return;
    }
    const buf = createDelayBuffer(
      live,
      delayRef.current,
      (message) => {
        // Degrade to live: a delayed feed that never plays is worse than an
        // early one, and the operator sees the warning.
        setError(message);
        delayBufferRef.current?.destroy();
        delayBufferRef.current = null;
        delayRef.current = 0;
        setDelaySeconds(0);
        setDelayReady(false);
        outgoingRef.current = streamRef.current;
        swapOutgoingTrack();
      },
      () => setDelayReady(true),
    );
    if (!buf) {
      outgoingRef.current = live;
      return;
    }
    delayBufferRef.current = buf;
    outgoingRef.current = buf.stream;
  }, [swapOutgoingTrack]);

  const applyDelay = useCallback(
    (next: unknown) => {
      const seconds = Math.max(0, Math.min(300, Number(next) || 0));
      if (seconds === delayRef.current && outgoingRef.current) return;
      delayRef.current = seconds;
      setDelaySeconds(seconds);
      if (!streamRef.current) return;
      buildOutgoing();
      swapOutgoingTrack();
    },
    [buildOutgoing, swapOutgoingTrack],
  );

  /** One non-trickle offer per HUD client; see LAN sender page for the
   *  reasoning behind the answer timeout (an unanswered offer never reaches
   *  the "failed" connection state — it just sits in "new"). */
  const createOfferFor = useCallback(
    async (hudId: string, retryCount?: number) => {
      const prev = peersRef.current.get(hudId);
      const retries = retryCount !== undefined ? retryCount : prev ? prev.retries : 0;
      destroyPeer(hudId);
      if (!streamRef.current) return;
      if (!outgoingRef.current) buildOutgoing();
      const stream = outgoingRef.current;
      if (!stream) return;
      // With a delay set, the buffer needs a full delay-length before it
      // emits a track. Hold the offer until then — the HUD would otherwise
      // receive a trackless peer that never produces video.
      const track = await waitForVideoTrack(stream, (delayRef.current + 20) * 1000);
      if (!track) {
        setError("The delayed video never started — try Stop and Start again.");
        return;
      }
      // A delay change or device swap superseded this offer while we waited.
      if (outgoingRef.current !== stream || peersRef.current.get(hudId)) return;

      // Ask for relay credentials only once a direct attempt has failed. The
      // operator's PowerHuds may refuse (relay off / budget spent) — that's a
      // normal answer, and we simply keep trying directly.
      if (wantTurnRef.current && !turnRef.current) {
        const granted = await new Promise<RTCIceServer[] | null>((resolve) => {
          const timer = window.setTimeout(() => resolve(null), 8000);
          const once = (servers: RTCIceServer[] | null, why: string | null) => {
            window.clearTimeout(timer);
            if (why) setRelayNote(why);
            resolve(servers);
          };
          turnResolverRef.current = once;
          sendEvent("powerhuds:camera_turn_request");
        });
        turnResolverRef.current = null;
        if (granted && granted.length) {
          turnRef.current = granted;
          setRelayNote("Direct connection failed — relaying through PowerStats.");
        }
      }
      const iceServers = turnRef.current
        ? [...DIRECT_ICE, ...turnRef.current]
        : DIRECT_ICE;

      const pc = new RTCPeerConnection({ iceServers });
      const entry: PeerEntry = {
        pc,
        state: "new",
        retries,
        answerTimer: null,
        retryScheduled: false,
      };
      peersRef.current.set(hudId, entry);

      const scheduleRetry = () => {
        if (entry.retryScheduled) return;
        if (entry.retries >= MAX_RETRIES) return;
        if (peersRef.current.get(hudId) !== entry) return;
        entry.retryScheduled = true;
        window.setTimeout(
          () => {
            if (peersRef.current.get(hudId) === entry) {
              createOfferFor(hudId, entry.retries + 1).catch(() => undefined);
            }
          },
          2000 * (entry.retries + 1),
        );
      };

      pc.addTrack(track, stream);
      pc.addEventListener("connectionstatechange", () => {
        entry.state = pc.connectionState;
        if (pc.connectionState === "connected") {
          entry.retries = 0;
          entry.retryScheduled = false;
        }
        if (pc.connectionState === "connected") {
          // Tell the operator whether this feed is direct or relayed, and
          // that video is genuinely flowing (not merely registered).
          sendEvent("powerhuds:camera_status", {
            streaming: true,
            relayed: !!turnRef.current,
          });
        }
        renderPeerStates();
        // "disconnected" is often transient and recovers on its own; only a
        // real "failed" warrants tearing the peer down and re-offering.
        if (pc.connectionState === "failed") {
          // Direct didn't work — next attempt may use a relay.
          wantTurnRef.current = true;
          scheduleRetry();
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      // Non-trickle: wait for ICE gathering so the single offer carries every
      // candidate (the HUD side never processes trickled candidates).
      if (pc.iceGatheringState !== "complete") {
        await new Promise<void>((resolve) => {
          const timer = window.setTimeout(resolve, 2500);
          pc.addEventListener("icegatheringstatechange", () => {
            if (pc.iceGatheringState === "complete") {
              window.clearTimeout(timer);
              resolve();
            }
          });
        });
      }
      if (peersRef.current.get(hudId) !== entry || !pc.localDescription) return;
      sendEvent("powerhuds:camera_offer", {
        hudId,
        steamid: steamidRef.current,
        offer: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
      });
      entry.answerTimer = window.setTimeout(() => {
        if (peersRef.current.get(hudId) === entry && !pc.remoteDescription) {
          scheduleRetry();
        }
      }, ANSWER_TIMEOUT_MS);
      renderPeerStates();
    },
    [destroyPeer, renderPeerStates, sendEvent, buildOutgoing, waitForVideoTrack],
  );

  const handleCameraEvent = useCallback(
    (event: string, args: unknown[]) => {
      const arg = (args && args[0]) as Record<string, unknown> | undefined;
      switch (event) {
        case "powerhuds:camera_roster": {
          const players = (arg?.availablePlayers as RosterEntry[]) || [];
          setRoster(players);
          if (typeof arg?.delaySeconds === "number" && !streamRef.current) {
            // Pre-registration: just show it; the buffer is built on register.
            delayRef.current = arg.delaySeconds;
            setDelaySeconds(arg.delaySeconds);
          }
          break;
        }
        case "powerhuds:camera_delay": {
          applyDelay(arg?.delaySeconds);
          break;
        }
        case "powerhuds:camera_turn": {
          const servers = (arg?.iceServers as RTCIceServer[] | null) ?? null;
          const reason = (arg?.reason as string | null) ?? null;
          turnResolverRef.current?.(servers, reason);
          break;
        }
        case "powerhuds:camera_register_result": {
          if (arg && arg.ok === false) {
            setError(String(arg.error || "Registration failed."));
            registeredRef.current = false;
          } else {
            setError("");
            registeredRef.current = true;
            // Build the buffer from the ack BEFORE any offer_request lands,
            // so the first peer already carries the delayed track.
            if (arg && typeof arg.delaySeconds === "number") {
              applyDelay(arg.delaySeconds);
            }
          }
          break;
        }
        case "powerhuds:camera_offer_request": {
          if (registeredRef.current && streamRef.current && arg?.hudId) {
            createOfferFor(String(arg.hudId)).catch((e: Error) =>
              setError("Connection failed: " + e.message),
            );
          }
          break;
        }
        case "powerhuds:camera_answer": {
          const p = arg?.hudId ? peersRef.current.get(String(arg.hudId)) : undefined;
          if (p && arg?.offer) {
            if (p.answerTimer) {
              window.clearTimeout(p.answerTimer);
              p.answerTimer = null;
            }
            p.pc
              .setRemoteDescription(arg.offer as RTCSessionDescriptionInit)
              .catch((e: Error) => setError("Answer failed: " + e.message));
          }
          break;
        }
        case "powerhuds:camera_hud_gone": {
          if (arg?.hudId) destroyPeer(String(arg.hudId));
          break;
        }
        case "powerhuds:camera_kicked": {
          kickedRef.current = true;
          setPhase("kicked");
          setError(String(arg?.reason || "Another device took over this player's camera."));
          teardownMedia();
          wsRef.current?.close();
          break;
        }
        default:
          break; // forward compatible
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createOfferFor, destroyPeer, applyDelay],
  );

  // ---------------------------------------------------------------------
  // Relay connection (with reconnect — rolling deploys drop sockets)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }
    let disposed = false;

    const connect = () => {
      if (disposed || kickedRef.current) return;
      // Only the first attempt announces "connecting" — during a retry loop
      // against a dark/unreachable relay the phase stays "unavailable"
      // instead of flapping back to "connecting" every attempt.
      if (reconnectAttemptRef.current === 0) setPhase("connecting");
      let opened = false;
      const ws = new WebSocket(playerWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        opened = true;
        reconnectAttemptRef.current = 0;
        ws.send(JSON.stringify({ t: "join", token }));
      };

      ws.onmessage = (raw) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(String(raw.data));
        } catch {
          return;
        }
        switch (msg.t) {
          case "joined":
            setPhase(msg.code === "operator_online" ? "ready" : "offline");
            if (msg.code === "operator_online") {
              sendEvent("powerhuds:camera_hello");
              if (registeredRef.current && steamidRef.current) {
                sendEvent("powerhuds:camera_register", { steamid: steamidRef.current });
              }
            }
            break;
          case "operator_online":
            setPhase("ready");
            sendEvent("powerhuds:camera_hello");
            // The operator restarted: our old peers are dead there. Re-register
            // and let fresh offer_requests rebuild them.
            destroyAllPeers();
            if (registeredRef.current && steamidRef.current) {
              sendEvent("powerhuds:camera_register", { steamid: steamidRef.current });
            }
            break;
          case "operator_offline":
            setPhase("offline");
            destroyAllPeers();
            break;
          case "error":
            if (msg.code === "invalid_token") {
              kickedRef.current = true; // don't reconnect a dead link
              setPhase("invalid");
            } else if (msg.code === "room_full") {
              setError("This camera room is full (12 players max).");
            }
            break;
          case "msg":
            handleCameraEvent(String(msg.event || ""), (msg.args as unknown[]) || []);
            break;
          default:
            break;
        }
      };

      ws.onclose = () => {
        if (disposed || kickedRef.current) return;
        destroyAllPeers();
        // Handshake never succeeded → relay dark/unreachable; an established
        // connection that dropped → transient, show "connecting".
        if (!opened) setPhase("unavailable");
        else setPhase("connecting");
        const attempt = Math.min(reconnectAttemptRef.current + 1, 6);
        reconnectAttemptRef.current = attempt;
        window.setTimeout(connect, 1500 * attempt);
      };
    };

    connect();
    return () => {
      disposed = true;
      wsRef.current?.close();
      destroyAllPeers();
      teardownMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------------------------------------------------------------------
  // Camera / media
  // ---------------------------------------------------------------------
  const teardownMedia = () => {
    delayBufferRef.current?.destroy();
    delayBufferRef.current = null;
    outgoingRef.current = null;
    setDelayReady(false);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (previewRef.current) previewRef.current.srcObject = null;
    setStreaming(false);
    registeredRef.current = false;
  };

  const startCamera = async (id?: string) => {
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: id
        ? { deviceId: { exact: id }, width: { ideal: 1280 }, height: { ideal: 720 } }
        : { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
    };
    const next = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = next;
    // The local preview always shows the LIVE camera — the player needs to
    // frame themselves, not watch a minute-old picture of themselves.
    if (previewRef.current) {
      previewRef.current.srcObject = next;
      previewRef.current.play().catch(() => undefined);
    }
    // Rebuild the delay pipeline around the new device, then hot-swap the
    // outgoing track into every live peer.
    buildOutgoing();
    swapOutgoingTrack();
  };

  const onStart = async () => {
    setError("");
    if (!steamid) {
      setError("Pick your name first.");
      return;
    }
    try {
      steamidRef.current = steamid;
      await startCamera(deviceId || undefined);
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "videoinput"));
      registeredRef.current = true;
      sendEvent("powerhuds:camera_register", { steamid });
      setStreaming(true);
    } catch (e) {
      setError("Could not start camera: " + (e as Error).message);
    }
  };

  const onStop = () => {
    destroyAllPeers();
    teardownMedia();
  };

  const onDeviceChange = (id: string) => {
    setDeviceId(id);
    if (streamRef.current) {
      startCamera(id).catch((e: Error) => setError("Camera switch failed: " + e.message));
    }
  };

  // ---------------------------------------------------------------------
  // Render (chrome-less, dark, self-contained)
  // ---------------------------------------------------------------------
  const peerRows = Object.entries(peerStates);

  const statusLine = (() => {
    switch (phase) {
      case "connecting":
        return { color: "#d9a648", text: "Connecting to PowerStats…" };
      case "offline":
        return { color: "#d9a648", text: "Broadcast PC is offline — waiting for it to come back." };
      case "ready":
        return { color: "#4fc26a", text: "Connected to the broadcast." };
      case "kicked":
        return { color: "#e05b4e", text: "Another device took over." };
      case "invalid":
        return { color: "#e05b4e", text: "This camera link is invalid or expired. Ask for a fresh one." };
      case "unavailable":
        return { color: "#e05b4e", text: "Online cameras are not available right now." };
      default:
        return { color: "#888", text: "" };
    }
  })();

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.brand}>
          POWERSTATS <span style={{ color: ACCENT }}>CAMERA</span>
        </div>
        <h1 style={styles.h1}>Player camera</h1>
        <p style={styles.muted}>
          Pick who you are, allow camera access, and keep this page open during the match.
        </p>

        <div style={{ ...styles.statusRow }}>
          <span style={{ ...styles.dot, background: statusLine.color }} />
          <span style={{ color: "#c9ccd4", fontSize: 14 }}>{statusLine.text}</span>
        </div>

        {phase !== "invalid" && phase !== "kicked" && phase !== "unavailable" && (
          <>
            <label style={styles.label} htmlFor="cam-player">
              I am
            </label>
            <select
              id="cam-player"
              style={styles.select}
              value={steamid}
              onChange={(e) => setSteamid(e.target.value)}
              disabled={streaming || roster.length === 0}
            >
              <option value="">
                {roster.length === 0 ? "Waiting for the player list…" : "Select your name…"}
              </option>
              {roster.map((p) => (
                <option key={p.steamid} value={p.steamid}>
                  {p.label || p.steamid}
                </option>
              ))}
            </select>

            <label style={styles.label} htmlFor="cam-device">
              Camera
            </label>
            <select
              id="cam-device"
              style={styles.select}
              value={deviceId}
              onChange={(e) => onDeviceChange(e.target.value)}
              disabled={devices.length === 0}
            >
              {devices.length === 0 ? (
                <option value="">Allow camera access first</option>
              ) : (
                devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </option>
                ))
              )}
            </select>

            {!streaming ? (
              <button style={styles.primaryBtn} onClick={onStart} disabled={phase !== "ready"}>
                Start my camera
              </button>
            ) : (
              <button style={styles.ghostBtn} onClick={onStop}>
                Stop
              </button>
            )}

            <video ref={previewRef} style={styles.video} autoPlay muted playsInline />

            {streaming && (
              <div style={{ marginTop: 14 }}>
                {delaySeconds > 0 && (
                  <div style={styles.statusRow}>
                    <span
                      style={{
                        ...styles.dot,
                        background: delayReady ? "#4fc26a" : "#d9a648",
                      }}
                    />
                    <span style={{ color: "#c9ccd4", fontSize: 14 }}>
                      {delayReady
                        ? `Delayed by ${delaySeconds}s to match the broadcast`
                        : `Building ${delaySeconds}s delay buffer…`}
                    </span>
                  </div>
                )}
                {relayNote && (
                  <div style={styles.statusRow}>
                    <span style={{ ...styles.dot, background: "#8b93a7" }} />
                    <span style={{ color: "#8b93a7", fontSize: 13 }}>{relayNote}</span>
                  </div>
                )}
                {peerRows.length === 0 ? (
                  <div style={styles.statusRow}>
                    <span style={{ ...styles.dot, background: "#d9a648" }} />
                    <span style={{ color: "#c9ccd4", fontSize: 14 }}>
                      Waiting for the overlay to connect…
                    </span>
                  </div>
                ) : (
                  peerRows.map(([hudId, state]) => (
                    <div key={hudId} style={styles.statusRow}>
                      <span
                        style={{
                          ...styles.dot,
                          background:
                            state === "connected"
                              ? "#4fc26a"
                              : state === "failed"
                                ? "#e05b4e"
                                : "#d9a648",
                        }}
                      />
                      <span style={{ color: "#c9ccd4", fontSize: 14 }}>
                        {state === "connected" ? "Streaming to overlay" : `Connecting… (${state})`}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        <div style={styles.error}>{error}</div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#1a1a1a",
    color: "#e8eaef",
    display: "flex",
    justifyContent: "center",
    padding: "24px 16px 48px",
    fontFamily:
      '-apple-system, "Segoe UI", system-ui, Roboto, "Helvetica Neue", sans-serif',
  },
  shell: { width: "100%", maxWidth: 520 },
  brand: {
    fontSize: 13,
    letterSpacing: "0.2em",
    fontWeight: 700,
    color: "#8b93a7",
    marginBottom: 18,
  },
  h1: { fontSize: 22, margin: "0 0 10px", color: "#fff" },
  muted: { color: "#8b93a7", fontSize: 14, marginBottom: 12 },
  label: {
    display: "block",
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#8b93a7",
    margin: "18px 0 6px",
  },
  select: {
    width: "100%",
    fontSize: 16,
    borderRadius: 8,
    padding: "12px 14px",
    background: "#242424",
    color: "#e8eaef",
    border: "1px solid #3a3a3a",
  },
  primaryBtn: {
    width: "100%",
    fontSize: 16,
    borderRadius: 8,
    padding: "12px 14px",
    background: ACCENT,
    color: "#fff",
    fontWeight: 700,
    border: "none",
    marginTop: 22,
    cursor: "pointer",
  },
  ghostBtn: {
    width: "100%",
    fontSize: 16,
    borderRadius: 8,
    padding: "12px 14px",
    background: "transparent",
    border: "1px solid #3a3a3a",
    color: "#8b93a7",
    marginTop: 22,
    cursor: "pointer",
  },
  video: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: 10,
    marginTop: 18,
    objectFit: "cover",
  },
  statusRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 0" },
  dot: { width: 9, height: 9, borderRadius: "50%", flex: "none" },
  error: { color: "#e05b4e", marginTop: 14, fontSize: 14, minHeight: 20 },
};

export default CameraSenderPage;
